import React, { useEffect, useState, useMemo, useContext, useRef } from 'react'
import { EventListContext } from '../../../utils/context'
import { useDispatch, useSelector } from 'react-redux'
import styles from './styles.module.scss'
import Button from '../../../components/Button'
import { Input, MultiSelect } from '../../../components/ModalInputs'
import Table from '../../../components/Table'
import ModalFooter from './Components/ModalFooter'
import { getEventClassesById, getEventMembers } from '../../../actions/events_list'
import { getSchoolYears } from '../../../actions/common'
import { getGroupTeam } from '../../../actions/groups'
import Confirmation from '../../../components/Confirmation/Confirmation'

export default function Participants() {
  const dispatch = useDispatch()
  const { closeModal, event, formik } = useContext(EventListContext)
  const { eventClassesWithMembers, eventMembers } = useSelector(state => state.events_list)
  const { schoolYears } = useSelector(state => state.common);
  const { groupTeam } = useSelector(state => state.groups);

  const { values, setFieldValue, handleSubmit } = formik
  const members = values.members || []

  const [searchFio, setSearchFio] = useState('')
  const [selectedClasses, setSelectedClasses] = useState([])
  const [localGroupTeam, setLocalGroupTeam] = useState([])
  const pendingGidRef = useRef(null)
  const [expandedClass, setExpandedClass] = useState(null)
  const [collapsedClasses, setCollapsedClasses] = useState(new Set())
  const [filterMode, setFilterMode] = useState('all') // 'all', 'participants', 'withResult'
  const [showCloseConfirm, setShowCloseConfirm] = useState(false)

  // Загрузка данных при монтировании
  useEffect(() => {
    dispatch(getSchoolYears())
    if (event?.nid) {
      dispatch(getEventClassesById(event.nid))
      // const year = getCurrentYearUtils(schoolYears, event.date)
      // dispatch(getEventMembers(year, event.mid))
    }

  }, [event?.nid, dispatch])

  useEffect(() => {
    if (groupTeam && pendingGidRef.current !== null) {
      const gid = pendingGidRef.current
      setLocalGroupTeam(prev => [
        ...prev.filter(m => m.gid !== gid),
        ...groupTeam.map(m => ({ ...m, gid }))
      ])
      pendingGidRef.current = null
    }
  }, [groupTeam])

  // Синхронизация formik с Redux при загрузке
  useEffect(() => {
    if (eventMembers && eventMembers.length > 0 && members.length === 0) {
      setFieldValue('members', eventMembers.map(m => ({
        mid: m.mid,
        is_plan: m.is_plan ? 1 : 0,
        is_fact: m.is_fact ? 1 : 0,
        is_winner: m.is_winner ? 1 : 0,
        is_prizer: m.is_prizer ? 1 : 0,
        comment: m.note || '',
        calc_fio: m.calc_fio,
        gid: m.gid,
        place: m.place,
        achievement: m.achievement,
        teacher_fio: m.teacher_fio,
        is_in_person: m.is_in_person,
        is_correspondence: m.is_correspondence,
        is_remote: m.is_remote
      })))
    }
  }, [eventMembers])

  // Фильтрация участников по ФИО и режиму
  const filteredMembers = useMemo(() => {
    let result = members

    // Фильтр по ФИО
    if (searchFio) {
      result = result.filter(m =>
        m.calc_fio?.toLowerCase().includes(searchFio.toLowerCase())
      )
    }

    // Фильтр по выбранным классам
    if (selectedClasses.length > 0) {
      result = result.filter(m =>
        selectedClasses.some(c => c.gid === m.gid)
      )
    }

    // Фильтр по режиму
    if (filterMode === 'participants') {
      result = result.filter(m => m.is_fact || m.is_plan)
    } else if (filterMode === 'withResult') {
      result = result.filter(m => m.is_prizer || m.is_winner || m.place)
    }

    return result
  }, [members, searchFio, selectedClasses, filterMode])

  // Выбор класса
  const handleSelectClass = (classData) => {
    if (selectedClasses.find(item => item.gid === classData.gid)) {
      setLocalGroupTeam(prev => prev.filter(m => m.gid !== classData.gid))
      setSelectedClasses(selectedClasses.filter(item => item.gid !== classData.gid))
    } else {
      pendingGidRef.current = classData.gid
      dispatch(getGroupTeam(classData.gid))
      setSelectedClasses([...selectedClasses, classData])
    }
  }

  // Развернуть/свернуть класс
  const handleExpandClass = (gid) => {
    setExpandedClass(expandedClass === gid ? null : gid)
  }

  const toggleClassCollapse = (gid) => {
    setCollapsedClasses(prev => {
      const next = new Set(prev)
      next.has(gid) ? next.delete(gid) : next.add(gid)
      return next
    })
  }

  // Обновление поля участника
  const handleMemberChange = (memberId, field, value) => {
    const numericValue = typeof value === 'boolean' ? (value ? 1 : 0) : value
    const memberIndex = members.findIndex(m => m.mid === memberId)
    if (memberIndex !== -1) {
      setFieldValue(`members[${memberIndex}].${field}`, numericValue)
    } else {
      const src = localGroupTeam.find(m => m.mid === memberId) || {}
      const newMember = {
        mid: memberId,
        is_plan: 0,
        is_fact: 0,
        is_winner: 0,
        is_prizer: 0,
        comment: '',
        calc_fio: src.calc_fio || '',
        gid: src.gid,
        [field]: numericValue,
      }
      setFieldValue('members', [...members, newMember])
    }
  }

  // Массовые действия
  const handleSetAll = (type) => {
    const membersToUpdate = selectedClasses.length > 0
      ? members.filter(m => selectedClasses.some(c => c.gid === m.gid))
      : members

    const updated = members.map(m => {
      if (membersToUpdate.find(um => um.mid === m.mid)) {
        if (type === 'all') {
          return { ...m, is_plan: 1, is_fact: 1 }
        } else if (type === 'plan') {
          return { ...m, is_plan: 1 }
        } else if (type === 'fact') {
          return { ...m, is_fact: 1 }
        }
      }
      return m
    })
    setFieldValue('members', updated)
  }

  // Перенести План в Факт
  const handleTransferToFact = () => {
    const updated = members.map(m => {
      if (m.is_plan && !m.is_fact) {
        return { ...m, is_fact: 1 }
      }
      return m
    })
    setFieldValue('members', updated)
  }

  // Получить участников класса
  const getClassMembers = (gid) => {
    return members.filter(m => m.gid === gid)
  }

  // Рендер строки участника
  const renderMemberRow = (member) => {
    const fm = members.find(m => m.mid === member.mid) || {}
    return (
      <tr key={member.mid}>
        <td style={{ paddingLeft: '24px' }}>{member.calc_fio}</td>
        <td>
          <input
            type="checkbox"
            checked={!!fm.is_plan}
            onChange={(e) => handleMemberChange(member.mid, 'is_plan', e.target.checked)}
          />
        </td>
        <td>
          <input
            type="checkbox"
            checked={!!fm.is_fact}
            onChange={(e) => handleMemberChange(member.mid, 'is_fact', e.target.checked)}
          />
        </td>
        <td>
          <input
            type="checkbox"
            checked={!!fm.is_prizer}
            onChange={(e) => handleMemberChange(member.mid, 'is_prizer', e.target.checked)}
          />
        </td>
        <td>
          <input
            type="checkbox"
            checked={!!fm.is_winner}
            onChange={(e) => handleMemberChange(member.mid, 'is_winner', e.target.checked)}
          />
        </td>
        <td>
          <input
            type="number"
            value={member.place || ''}
            onChange={(e) => handleMemberChange(member.mid, 'place', e.target.value)}
            style={{ width: '50px' }}
          />
        </td>
        <td>
          <input
            type="text"
            value={member.achievement || ''}
            onChange={(e) => handleMemberChange(member.mid, 'achievement', e.target.value)}
            style={{ width: '100px' }}
          />
        </td>
        <td>
          <input
            type="text"
            value={member.note || ''}
            onChange={(e) => handleMemberChange(member.mid, 'note', e.target.value)}
            style={{ width: '100px' }}
          />
        </td>
        <td>{member.teacher_fio || ''}</td>
        <td>
          <input
            type="checkbox"
            checked={!!fm.is_in_person}
            onChange={(e) => handleMemberChange(member.mid, 'is_in_person', e.target.checked)}
          />
        </td>
        <td>
          <input
            type="checkbox"
            checked={!!fm.is_correspondence}
            onChange={(e) => handleMemberChange(member.mid, 'is_correspondence', e.target.checked)}
          />
        </td>
        <td>
          <input
            type="checkbox"
            checked={!!fm.is_remote}
            onChange={(e) => handleMemberChange(member.mid, 'is_remote', e.target.checked)}
          />
        </td>
      </tr>
    )
  }

  return (
    <>
      <div className={styles.modalMain}>
        <div className={styles.tabWrapper}>
          <div className={styles.inputsRow}>
            <h3>Классы</h3>
            <Button onClick={() => handleSetAll('all')}>Выделить всех</Button>
            <Button onClick={() => handleSetAll('plan')}>Выделить всех План</Button>
            <Button onClick={() => handleSetAll('fact')}>Выделить всех Факт</Button>
          </div>

          <div className={styles.inputsRow} style={{ marginTop: '10px', alignItems: 'end' }}>
            <Input
              label="Поиск по ФИО"
              value={searchFio}
              onChange={(e) => setSearchFio(e.target.value)}
              style={{ width: '300px' }}
            />
            <MultiSelect
              label="Классы"
              options={eventClassesWithMembers?.map(c => ({
                value: c.gid,
                label: c.g_name + (c.cl !== 0 ? ' ✓' : '')
              })) || []}
              value={selectedClasses.map(c => ({
                value: c.gid,
                label: c.g_name + (c.cl !== 0 ? ' ✓' : '')
              }))}
              onChange={(selected) => {
                const newSelected = selected || []
                const added = newSelected.filter(s => !selectedClasses.find(c => c.gid === s.value))
                const removed = selectedClasses.filter(c => !newSelected.find(s => s.value === c.gid))
                removed.forEach(c => {
                  setLocalGroupTeam(prev => prev.filter(m => m.gid !== c.gid))
                })
                added.forEach(s => {
                  const classData = eventClassesWithMembers.find(c => c.gid === s.value)
                  if (classData) {
                    pendingGidRef.current = classData.gid
                    dispatch(getGroupTeam(classData.gid))
                  }
                })
                setSelectedClasses(newSelected.map(s => eventClassesWithMembers.find(c => c.gid === s.value)).filter(Boolean))
              }}
              style={{ minWidth: '300px' }}
            />
          </div>

          <div className={styles.rightClasses}>
              <Table wrapperStyle={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Участники</th>
                    <th>План</th>
                    <th>Факт</th>
                    <th>Призер</th>
                    <th>Победитель</th>
                    <th>Место</th>
                    <th>Достижения</th>
                    <th>Примечание</th>
                    <th>Руководители</th>
                    <th>Очно</th>
                    <th>Заочно</th>
                    <th>Дистанционно</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedClasses.map(classItem => {
                    const classMembers = localGroupTeam.filter(m => m.gid === classItem.gid)
                    const isCollapsed = collapsedClasses.has(classItem.gid)
                    return (
                      <React.Fragment key={classItem.gid}>
                        <tr
                          onClick={() => toggleClassCollapse(classItem.gid)}
                          style={{ backgroundColor: '#e8f0fe', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          <td colSpan={12} style={{ userSelect: 'none' }}>
                            {isCollapsed ? '▶ ' : '▼ '}{classItem.g_name}
                          </td>
                        </tr>
                        {!isCollapsed && classMembers.map(renderMemberRow)}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </Table>
          </div>

          <br />
          <div className={styles.inputsRow}>
            <Button onClick={() => window.print()}>Печать</Button>
            <Button
              onClick={() => setFilterMode(filterMode === 'participants' ? 'all' : 'participants')}
              style={{ backgroundColor: filterMode === 'participants' ? '#3689FF' : '' }}
            >
              Только участники
            </Button>
            <Button
              onClick={() => setFilterMode(filterMode === 'withResult' ? 'all' : 'withResult')}
              style={{ backgroundColor: filterMode === 'withResult' ? '#3689FF' : '' }}
            >
              Только с результатом
            </Button>
            <Button onClick={handleTransferToFact}>Перенести в ФАКТ</Button>
          </div>
        </div>
      </div>

      <ModalFooter
        event={event}
        onSave={handleSubmit}
        onClose={() => setShowCloseConfirm(true)}
        showCheckbox={false}
      />
      {showCloseConfirm && (
        <Confirmation
          title="Закрыть без сохранения изменений?"
          acceptConfirmation={() => { closeModal(); setShowCloseConfirm(false) }}
          rejectConfirmation={() => setShowCloseConfirm(false)}
        />
      )}
    </>
  )
}
